<template>
<div>
  <!--    Loader-->
  <loading-panel :loading="loading"></loading-panel>

  <base-alert-dialog
    :show="alertDialog.show"
    :variant="alertDialog.variant"
    :title="alertDialog.title"
    :message="alertDialog.message"
    @confirm="closeAlert"
    @cancel="alertDialog.show = false"
  />

  <!--    Breadcrumbs-->
  <breadcrumb style="margin-left: -15px">
    <breadcrumb-item>
      <a href="/projects">Projects</a>
    </breadcrumb-item>
    <breadcrumb-item>
      <span>{{ project.name }}</span>
    </breadcrumb-item>
  </breadcrumb>


  <div class="row">
    <div class="col-12">
      <card>
        <tab-rail
          :tabs="categories.map(c => ({ name: c.name, shortName: c.short_name }))"
          :value="activeTabIndex"
          @input="switchTab($event, perPage, getOffset())"
          class="mb-3"
        >
          <template #actions>
            <base-button v-if="hasPermissions('project_ticket_update')" type="primary" size="sm" v-on:click="createNewTicket()">
              <i class="tim-icons el-icon-plus mr-1"></i> <span class="d-none d-sm-inline">Create ticket</span>
            </base-button>

            <el-dropdown v-if="hasPermissions('project_update')">
              <base-button simple type="primary" link>
                <i style="font-size: medium" class="tim-icons el-icon-more"></i>
              </base-button>
              <template #dropdown>
                <el-dropdown-menu>
                  <a class="dropdown-item mt-2 mb-2" @click.prevent="configureWorkTypes()" href="#">
                    <i class="tim-icons icon-components text-default mr-2"></i> Configure ticket types
                  </a>
                </el-dropdown-menu>
              </template>
            </el-dropdown>
          </template>
        </tab-rail>
        <filter-bar
          :filters="filterSchema"
          :initial-value="filterValues"
          :result-count="totalRows"
          :loading="loading"
          :per-page="perPage"
          :per-page-options="perPageOptions"
          saved-filters-key="project-tickets"
          :saved-filters-entity="savedViewsEntity"
          @change="onFiltersChanged"
          @update:perPage="onPerPageChange"
        >
          <template #actions>
            <base-button v-if="hasPermissions('download_data')" icon class="btn-link text-white" v-on:click="downloadCSV()">
              <i class="tim-icons icon-cloud-download-93"></i>
            </base-button>
          </template>
        </filter-bar>

        <div class="mt-3" v-if="tickets.length <= 0">
          <div class="font-icon-detail">
              <span style="font-size: 3em">
                <i class="fas fa-exclamation-triangle"></i>
              </span>
            <h4 class="mt-3">No tickets match your search.</h4>
          </div>
        </div>

        <div class="row d-sm-block d-none" v-if="tickets.length > 0">
          <div class="col">
            <b-table striped hover responsive :items="tickets"
                     :fields="ticketTableFields">
              <template v-slot:cell(ticket)="row">
                <div class="d-flex align-items-center" style="max-width: 420px">
                  <span class="text-nowrap mr-2 flex-shrink-0">
                    <template v-if="row.item.parent_ticket_id">
                      <a class="text-muted" :href="'/projects/' + project.id + '/tickets/' + row.item.parent_ticket_id">{{row.item.parent_ticket_key}}</a>
                      <span class="text-muted"> / </span>
                    </template>
                    <a :href="'/projects/' + project.id + '/tickets/' + row.item.id">{{row.item.ticket_key}}</a>
                  </span>
                  <a class="text-white text-truncate" style="min-width: 0" :title="row.item.title"
                     :href="'/projects/' + project.id + '/tickets/' + row.item.id">{{row.item.title}}</a>
                </div>
              </template>
              <template v-slot:cell(ticket_type)="row">
                <badge :style="{'background-color': row.item.ticket_type_label_color}">{{row.item.ticket_type_name}}</badge>
              </template>
              <template v-slot:cell(assignee)="row">
                <span v-if="!row.item.assignee_id" class="text-muted">-</span>
                <span v-else>{{row.item.assignee_name}}</span>
              </template>
              <template v-slot:cell(reported_by)="row">
                <span v-if="!row.item.reporter_id" class="text-muted">-</span>
                <span v-else>{{row.item.reporter_name}}</span>
              </template>
              <template v-slot:cell(priority)="row">
                <i :class="getPriority(row.item.priority).icon" class="mr-1"
                   aria-hidden="true" :style="{'color' : getPriority(row.item.priority).color}"></i>{{ getPriority(row.item.priority).name }}
              </template>
              <template v-slot:cell(created)="row">
                {{ getLocalDateTime(row.item.time_created) }}
              </template>
              <template v-slot:cell(last_updated)="row">
                <span v-if="!row.item.last_updated" class="text-muted">-</span>
                <span v-else>{{ getLocalDateTime(row.item.last_updated) }}</span>
              </template>
              <template v-slot:cell(due_date)="row">
                <span v-if="!row.item.due_date" class="text-muted">-</span>
                <span v-else :style="{ color: isOverdue(row.item) ? '#ec250d' : 'inherit' }">
                  {{ getLocalDate(row.item.due_date) }}
                </span>
              </template>
              <template v-slot:cell(status)="row">
                <badge :style="{'background-color': row.item.label_color}">{{row.item.status_name}}</badge>
              </template>
              <template v-slot:cell(watchers)="row">
                <el-popover
                  placement="bottom-start"
                  width="350"
                  trigger="click"
                  popper-class="watcher-popover"
                  @show="loadTicketWatchers(row.item)">
                  <div class="watcher-popover-content">
                    <!-- Watch/Unwatch Action Link -->
                    <div>
                      <a
                        v-if="!row.item.watching"
                        href="#"
                        @click.prevent="modifyTicket(row.item, $event)"
                        class="dropdown-item mt-2 mb-2">
                        <i class="tim-icons el-icon-view text-default mr-2"></i> Start watching
                      </a>
                      <a
                        v-else
                        href="#"
                        @click.prevent="modifyTicket(row.item, $event)"
                        class="dropdown-item mt-2 mb-2">
                        <i class="tim-icons el-icon-view text-primary mr-2"></i> Stop watching
                      </a>
                      <hr class="my-2" style="border-color: rgba(0,0,0,0.1);">
                    </div>

                    <!-- Watchers List -->
                    <div v-if="selectedTicketWatchers.length > 0">
                      <div class="mb-2">
                        <small class="text-uppercase watcher-label">Watching this ticket</small>
                      </div>
                      <div
                        v-for="(watcher, index) in selectedTicketWatchers"
                        :key="'W' + index"
                        class="watcher-item">
                        <div class="d-flex align-items-center">
                          <img
                            v-if="watcher.img"
                            :src="watcher.img"
                            class="avatar mr-2"
                            alt="avatar"
                            style="width: 28px; height: 28px; object-fit: cover; border-radius: 50%;"
                          />
                          <div
                            v-else
                            class="avatar mr-2 d-flex align-items-center justify-content-center"
                            style="width: 28px; height: 28px; background-color: #1d8cf8; border-radius: 50%; font-weight: bold; color: white; font-size: 11px;">
                            {{getInitials(watcher.first_name, watcher.last_name)}}
                          </div>
                          <span class="watcher-name">{{watcher.first_name}} {{watcher.last_name}}</span>
                        </div>
                        <a
                          href="#"
                          @click.prevent="removeWatcher(watcher, row.item)"
                          class="text-default"
                          style="text-decoration: none; padding: 4px;">
                          <i class="tim-icons icon-simple-remove" style="font-size: 12px;"></i>
                        </a>
                      </div>
                    </div>

                    <!-- No Watchers Message -->
                    <div v-else class="no-watchers-message">
                      <p class="mb-0">No current watchers</p>
                    </div>

                    <!-- Add Watchers Section -->
                    <div>
                      <hr class="my-2" style="border-color: rgba(0,0,0,0.1);">
                      <div v-if="!showAddWatchers">
                        <a
                          href="#"
                          @click.prevent="showAddWatchers = true"
                          class="dropdown-item mt-2 mb-2">
                          <i class="tim-icons icon-simple-add text-default mr-2"></i> Add watchers
                        </a>
                      </div>
                      <div v-else>
                        <base-input style="margin-bottom: 10px;">
                          <el-select
                            v-model="selectedWatchersToAdd"
                            multiple
                            filterable
                            placeholder="Search users..."
                            class="select-primary"
                            style="width: 100%;"
                            size="small">
                            <el-option class="select-primary"
                              v-for="user in availableUsersForWatching"
                              :key="user.id"
                              :label="user.name"
                              :value="user.id">
                              <div class="d-flex align-items-center">
                                <img
                                  v-if="user.img"
                                  :src="user.img"
                                  class="avatar mr-2"
                                  alt="avatar"
                                  style="width: 20px; height: 20px; object-fit: cover; border-radius: 50%;"
                                />
                                <div
                                  v-else
                                  class="mr-2 d-flex align-items-center justify-content-center"
                                  style="width: 20px; height: 20px; background-color: #1d8cf8; border-radius: 50%; font-size: 9px; font-weight: bold; color: white;">
                                  {{getInitials(user.first_name, user.last_name)}}
                                </div>
                                <span style="font-size: 13px;">{{user.name}}</span>
                              </div>
                            </el-option>
                          </el-select>
                        </base-input>
                        <div class="d-flex" style="gap: 8px;">
                          <base-button size="sm" type="secondary" @click="showAddWatchers = false; selectedWatchersToAdd = [];" style="flex: 1;">Cancel</base-button>
                          <base-button size="sm" type="primary" @click="addWatchersInline(row.item)" style="flex: 1;">Add</base-button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <base-button
                    slot="reference"
                    size="sm"
                    :type="row.item.watching ? 'primary' : 'default'">
                    <i class="tim-icons el-icon-view mr-1"></i>
                    <span class="ml-1">{{row.item.watcher_count || 0}}</span>
                  </base-button>
                </el-popover>
              </template>
              <template v-slot:cell(actions)="row">
                <el-dropdown>
                  <i class="text-primary tim-icons el-icon-more"></i>
                  <template #dropdown>
                    <el-dropdown-menu>
                      <a target="_blank" class="dropdown-item mt-2 mb-2" :href="'/projects/' + projectId + '/tickets/' + row.item.id">
                        <i class="tim-icons icon-notes text-info mr-2"></i> Ticket details
                      </a>
                      <hr>
                      <a class="dropdown-item mt-2 mb-2" v-if="hasPermissions('project_ticket_archive')"
                         @click.prevent="archiveTicket(row.item)" href="#">
                        <i class="tim-icons icon-trash-simple mr-2 text-danger"></i> Archive ticket
                      </a>
                    </el-dropdown-menu>
                  </template>
                </el-dropdown>
              </template>
            </b-table>
          </div>
        </div>

        <div class="row d-sm-none d-block" v-if="tickets.length > 0">
          <div class="col">
            <b-table striped hover :items="tickets"
                     :fields="['ticket']">

              <template #head(ticket)="row">
                <span>Ticket count: {{totalRows}}</span>
              </template>
              <template v-slot:cell(ticket)="row">
                <div class="row">
                  <div class="col">
                    <h4 class="card-title">
                      <span class="text-nowrap">
                        <template v-if="row.item.parent_ticket_id">
                          <a class="text-muted" :href="'/projects/' + project.id + '/tickets/' + row.item.parent_ticket_id">{{row.item.parent_ticket_key}}</a>
                          <span class="text-muted"> / </span>
                        </template>
                        <a :href="'/projects/' + project.id + '/tickets/' + row.item.id">{{row.item.ticket_key}}</a>
                      </span>
                    </h4>
                  </div>
                  <div class="col text-right">
                    <badge :style="{'background-color': row.item.ticket_type_label_color}">{{row.item.ticket_type_name}}</badge>
                  </div>
                </div>
                <div class="row">
                  <div class="col">
                    <p>{{row.item.title}}</p>
                  </div>
                </div>

                <div class="row mt-1 mb-2">
                  <div class="col-3">
                    <badge :style="{'background-color': row.item.label_color}">{{row.item.status_name}}</badge>
                  </div>
                  <div class="col">
                    <i :class="getPriority(row.item.priority).icon" class="mr-1"
                       aria-hidden="true" :style="{'color' : getPriority(row.item.priority).color}"></i>
                    {{ getPriority(row.item.priority).name }}
                  </div>
                </div>
                <div class="row">
                  <div class="col">
                    <small>Created:  {{getLocalDateTime(row.item.time_created)}}</small>
                  </div>
                </div>
                <div class="row" v-if="row.item.last_updated">
                  <div class="col">
                    <small>Last updated:  {{getLocalDateTime(row.item.last_updated)}}</small>
                  </div>
                </div>
                <div class="row" v-if="row.item.due_date">
                  <div class="col">
                    <small :style="{ color: isOverdue(row.item) ? '#ec250d' : 'inherit' }">
                      Due: {{ getLocalDate(row.item.due_date) }}
                    </small>
                  </div>
                </div>
                <div class="row" v-if="row.item.assignee_id">
                  <div class="col">
                    <small>Assignee: {{row.item.assignee_name}}</small>
                  </div>
                </div>
                <div class="row" v-if="row.item.reporter_id">
                  <div class="col">
                    <small>Reporter: {{row.item.reporter_name}}</small>
                  </div>
                </div>
                <div class="row mt-2">
                  <div class="col">
                    <el-popover
                      placement="bottom-start"
                      width="350"
                      trigger="click"
                      popper-class="watcher-popover"
                      @show="loadTicketWatchers(row.item)">
                      <div class="watcher-popover-content">
                        <!-- Watch/Unwatch Action Link -->
                        <div>
                          <a
                            v-if="!row.item.watching"
                            href="#"
                            @click.prevent="modifyTicket(row.item, $event)"
                            class="dropdown-item mt-2 mb-2">
                            <i class="tim-icons el-icon-view text-default mr-2"></i> Start watching
                          </a>
                          <a
                            v-else
                            href="#"
                            @click.prevent="modifyTicket(row.item, $event)"
                            class="dropdown-item mt-2 mb-2">
                            <i class="tim-icons el-icon-view text-primary mr-2"></i> Stop watching
                          </a>
                          <hr class="my-2" style="border-color: rgba(0,0,0,0.1);">
                        </div>

                        <!-- Watchers List -->
                        <div v-if="selectedTicketWatchers.length > 0">
                          <div class="mb-2">
                            <small class="text-uppercase watcher-label">Watching this ticket</small>
                          </div>
                          <div
                            v-for="(watcher, index) in selectedTicketWatchers"
                            :key="'W' + index"
                            class="watcher-item">
                            <div class="d-flex align-items-center">
                              <img
                                v-if="watcher.img"
                                :src="watcher.img"
                                class="avatar mr-2"
                                alt="avatar"
                                style="width: 28px; height: 28px; object-fit: cover; border-radius: 50%;"
                              />
                              <div
                                v-else
                                class="avatar mr-2 d-flex align-items-center justify-content-center"
                                style="width: 28px; height: 28px; background-color: #1d8cf8; border-radius: 50%; font-weight: bold; color: white; font-size: 11px;">
                                {{getInitials(watcher.first_name, watcher.last_name)}}
                              </div>
                              <span class="watcher-name">{{watcher.first_name}} {{watcher.last_name}}</span>
                            </div>
                            <a
                              href="#"
                              @click.prevent="removeWatcher(watcher, row.item)"
                              class="text-danger"
                              style="text-decoration: none; padding: 4px;">
                              <i class="tim-icons icon-simple-remove" style="font-size: 12px;"></i>
                            </a>
                          </div>
                        </div>

                        <!-- No Watchers Message -->
                        <div v-else class="no-watchers-message">
                          <p class="mb-0">No current watchers</p>
                        </div>

                        <!-- Add Watchers Section -->
                        <div v-if="hasPermissions('project_watch')">
                          <hr class="my-2" style="border-color: rgba(0,0,0,0.1);">
                          <div v-if="!showAddWatchers">
                            <a
                              href="#"
                              @click.prevent="showAddWatchers = true"
                              class="dropdown-item mt-2 mb-2">
                              <i class="tim-icons icon-simple-add text-default mr-2"></i> Add watchers
                            </a>
                          </div>
                          <div v-else>
                            <base-input style="margin-bottom: 10px;">
                              <el-select
                                v-model="selectedWatchersToAdd"
                                multiple
                                filterable
                                placeholder="Search users..."
                                class="select-primary"
                                style="width: 100%;"
                                size="small">
                                <el-option
                                  v-for="user in availableUsersForWatching"
                                  :key="user.id"
                                  :label="user.name"
                                  :value="user.id">
                                  <div class="d-flex align-items-center">
                                    <img
                                      v-if="user.img"
                                      :src="user.img"
                                      class="avatar mr-2"
                                      alt="avatar"
                                      style="width: 20px; height: 20px; object-fit: cover; border-radius: 50%;"
                                    />
                                    <div
                                      v-else
                                      class="mr-2 d-flex align-items-center justify-content-center"
                                      style="width: 20px; height: 20px; background-color: #1d8cf8; border-radius: 50%; font-size: 9px; font-weight: bold; color: white;">
                                      {{getInitials(user.first_name, user.last_name)}}
                                    </div>
                                    <span style="font-size: 13px;">{{user.name}}</span>
                                  </div>
                                </el-option>
                              </el-select>
                            </base-input>
                            <div class="d-flex" style="gap: 8px;">
                              <base-button size="sm" type="secondary" @click="showAddWatchers = false; selectedWatchersToAdd = [];" style="flex: 1;">Cancel</base-button>
                              <base-button size="sm" type="primary" @click="addWatchersInline(row.item)" style="flex: 1;">Add</base-button>
                            </div>
                          </div>
                        </div>
                      </div>

                      <base-button
                        slot="reference"
                        size="sm"
                        :type="row.item.watching ? 'primary' : 'default'">
                        <i class="tim-icons el-icon-view mr-1"></i>
                        <span v-if="row.item.watching">Watching</span>
                        <span v-else>Watch</span>
                        <span class="ml-1">({{row.item.watcher_count || 0}})</span>
                      </base-button>
                    </el-popover>
                  </div>
                </div>
              </template>
            </b-table>
          </div>
        </div>

        <div class="d-flex justify-content-between align-items-center">
          <b-pagination v-on:input="getProject(activeTabIndex, perPage, getOffset())"
                        v-model="currentPage"
                        :total-rows="totalRows"
                        :per-page="perPage"
          ></b-pagination>
        </div>
      </card>
    </div>
  </div>
<!--  New ticket -->
  <base-drawer :show="modals.new_ticket" title="Create a ticket" @close="closeTicketDrawer">
    <drawer-section title="Ticket" icon="icon-notes">
      <base-input label="Ticket summary" :error="errs.summary" placeholder="Summary" required v-model="title"></base-input>

      <base-input required label="Ticket type" :error="errs.ticket_type">
        <el-select
          v-model="ticket_type"
          class="select-primary"
          value-key="id"
          placeholder="Ticket type">
          <el-option
            v-for="(t, i) in ticket_types.filter(t => t.archived === 0)"
            class="select-primary"
            :label="t.name"
            :value="{id: t.id, name: t.name}"
            :key="'TT' + i"
          ></el-option>
        </el-select>
      </base-input>

      <base-input required label="Priority" :error="errs.priority">
        <el-select
          v-model="priority"
          class="select-primary"
          value-key="id"
          placeholder="Priority">
          <el-option
            v-for="(pt, index) in priorities"
            class="select-primary"
            :label="pt.name"
            :value="{id: pt.id, name: pt.name, icon: pt.icon, color: pt.color}"
            :key="'PT' + index"
          >
            <span style="float: left">
              <i :class="pt.icon" class="mr-3" aria-hidden="true" :style="{'color' : pt.color}"></i>
              {{ pt.name }}
            </span>
          </el-option>
        </el-select>
      </base-input>

      <base-input required label="Status" :error="errs.status">
        <el-select
          v-model="status"
          class="select-primary"
          value-key="id"
          placeholder="Status">
          <el-option
            v-for="(s, index) in statuses"
            class="select-primary"
            :label="s.name"
            :value="{id: s.id, name: s.name, lifecycle_status: s.lifecycle_status, label_color: s.label_color}"
            :key="'S' + index"
          >
            <span style="float: left">
              <badge :style="{'background-color' : s.label_color}">{{ s.name }}</badge>
            </span>
          </el-option>
        </el-select>
      </base-input>

      <base-input label="Assignee" :error="errs.assignee">
        <el-select
          v-model="assignee"
          class="select-primary"
          value-key="id"
          placeholder="Assignee">
          <el-option
            v-for="(u, index) in users"
            class="select-primary"
            :label="u.name"
            :value="{id: u.id, name: u.name}"
            :key="'U' + index"
          ></el-option>
        </el-select>
      </base-input>
    </drawer-section>

    <drawer-section title="Planning" icon="icon-calendar-60">
      <base-input label="Due date">
        <el-date-picker
          v-model="new_ticket_due_date"
          type="date"
          placeholder="No due date"
          :format="day_of_week + date_format_cal"
          value-format="yyyy-MM-dd"
          clearable
          style="width: 100%">
        </el-date-picker>
      </base-input>

      <label>Time estimate
        <el-popover trigger="hover" placement="right">
          <div>
            <div class="popover-body" style="padding-bottom: 0px">
              <p class="text-black-50">Use the format: 2w 4d 6h 45m</p>
              <ul class="text-black-50 small">
                <li class="text-black-50">w = weeks</li>
                <li class="text-black-50">d = days</li>
                <li class="text-black-50">h = hours</li>
                <li class="text-black-50">m = minutes</li>
              </ul>
            </div>
          </div>
          <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
        </el-popover>
      </label>
      <base-input v-model="time_estimate" :error="errs.time_estimate"></base-input>
    </drawer-section>

    <drawer-section title="Details" icon="icon-pencil">
      <rich-text-editor
        ref="descriptionEditor"
        v-model="descriptionHtml"
        toolbar="comment"
        placeholder="Add ticket description..."
        height="auto"
        :dark="isDarkMode"
      />
    </drawer-section>

    <template #footer>
      <div class="footer-actions">
        <base-button type="secondary" size="sm" @click="closeTicketDrawer">Cancel</base-button>
        <base-button type="primary" size="sm" @click="createTicket()">Create</base-button>
      </div>
    </template>
  </base-drawer>

<!--  Work types-->
  <modal :show.sync="modals.configure_work_types"
         body-classes="p-0"
         modal-classes="modal-dialog-centered modal-lg">
    <card type="secondary"
          header-classes="bg-white pb-5"
          body-classes="px-lg-5 py-lg-5"
          class="border-0 mb-0">

      <div class="row">
        <div class="col">
          <h4 class="card-title">Configure work types</h4>
        </div>
        <div class="col text-right" v-if="!edit_ticket_types">
          <base-button size="sm" class="ml-2" type="primary" @click="addNewTicketType()"><i class="tim-icons el-icon-plus mr-1"></i>
            Add new ticket type</base-button>
        </div>
      </div>


      <div v-if="!edit_ticket_types">
        <div class="row">
          <div class="col">
            <b-table striped hover :items="ticket_types.filter(t => t.archived === 0)"
                     :fields="['ticket_type', 'description', 'actions']">
              <template v-slot:cell(ticket_type)="row">
                <div class="row">
                  <div class="col">
                    <badge :style="{'background-color': row.item.label_color}">{{row.item.name}}</badge>
                  </div>
                </div>
              </template>
              <template v-slot:cell(description)="row">
                <span v-if="row.item.description">{{row.item.description}}</span>
                <span v-else>-</span>
              </template>
              <template v-slot:cell(actions)="row">
                <el-dropdown>
                  <i class="text-primary tim-icons el-icon-more"></i>
                  <template #dropdown>
                    <el-dropdown-menu>
                      <a class="dropdown-item mt-2 mb-2" @click.prevent="editTicketType(row.item)" href="#">
                        <i class="tim-icons icon-pencil mr-2 text-warning"></i> Edit ticket type
                      </a>
                      <hr>
                      <a class="dropdown-item mt-2 mb-2" @click.prevent="archiveTicketType(row.item)" href="#">
                        <i class="tim-icons icon-trash-simple mr-2 text-danger"></i> Archive ticket type
                      </a>
                    </el-dropdown-menu>
                  </template>
                </el-dropdown>
              </template>
            </b-table>
          </div>
        </div>

        <div class="row text-center">
          <div class="col">
            <base-button type="secondary" @click="modals.configure_work_types = false">Cancel</base-button>
          </div>
        </div>
      </div>
      <div v-if="edit_ticket_types">
        <div class="row">
          <div class="col-lg-7">
            <base-input label="Ticket type name" :error="errs.ticket_type_name" placeholder="Ticket type name" required v-model="ticket_type_name"></base-input>
          </div>
          <div class="col-lg-5">
            <base-input label="Label color">
              <el-color-picker v-model="ticket_type_label_color" show-alpha></el-color-picker>
            </base-input>
          </div>
        </div>

        <div class="row">
          <div class="col">
            <base-input label="Ticket type description" placeholder="Description"  v-model="ticket_type_description"></base-input>
          </div>
        </div>

        <div class="row text-center mt-3">
          <div class="col">
            <base-button type="secondary" @click="edit_ticket_types = false">Cancel</base-button>
            <base-button v-if="selectedTicketType.hasOwnProperty('id')" class="ml-2" type="primary" @click="updateTicketType()">
              Update</base-button>
            <base-button v-else class="ml-2" type="primary" @click="createTicketType()">
              Create</base-button>
          </div>
        </div>
      </div>


    </card>
  </modal>

</div>
</template>

<script>
import {BPagination, BTable} from "bootstrap-vue";
import {ColorPicker, Dropdown, DropdownMenu, Popover} from "element-ui";
import BreadcrumbItem from "@/components/Breadcrumb/BreadcrumbItem";
import Breadcrumb from "@/components/Breadcrumb/Breadcrumb";
import TabRail from '@/components/TabRail';
import {date as formatDate, formatGroupDate, formatGroupTime} from "@/plugins/dates";
import {Auth} from "aws-amplify";
import {API_LOCATION} from "@/api-config";
import axios from "axios";
import {Badge} from "@/components";
import _ from "lodash";
import RichTextEditor from "@/components/RichTextEditor";

export default {
  name: "ProjectDetails",
  components: {
    TabRail,
    BTable,
    BPagination,
    [Dropdown.name]: Dropdown,
    [DropdownMenu.name]: DropdownMenu,
    [ColorPicker.name]: ColorPicker,
    [Popover.name]: Popover,
    BreadcrumbItem,
    Breadcrumb,
    Badge,
    RichTextEditor
  },
  computed: {
    // The project this board's saved views belong to; FilterBar stores it in the
    // saved blob so the saved views drawer can open the right board.
    savedViewsEntity() {
      return { id: this.$route.params.id, name: this.project && this.project.name ? this.project.name : undefined }
    },
    categories() {
      return [{name: 'Open tickets', short_name: 'Open'},
        {name: 'My tickets', short_name: 'My tickets'},
        {name: 'In progress', short_name: 'In progress'},
        {name: 'Done', short_name: 'Done'},
      ]
    },
    filterSchema() {
      // All filters are primary so they show inline with no "More filters" overflow.
      const schema = [
        {key: 'key', type: 'text', label: 'Ticket key', placeholder: 'Ticket key', operatorLabel: 'contains', primary: true},
        {key: 'summary', type: 'text', label: 'Summary', placeholder: 'Summary', operatorLabel: 'contains', primary: true},
        {key: 'ticket_type', type: 'select', label: 'Ticket type', primary: true,
          options: this.ticket_types.map(t => ({label: t.name, value: t.id}))},
        {key: 'show_subtasks', type: 'toggle', label: 'Show subtasks', primary: true},
      ]
      // Mirrors the original: these were hidden on the special tab 4.
      if (this.activeTabIndex !== 4) {
        schema.push({key: 'reported_by', type: 'text', label: 'Reported by', placeholder: 'Reported by', operatorLabel: 'contains', primary: true})
        schema.push({key: 'assigned_to', type: 'text', label: 'Assigned to', placeholder: 'Assigned to', operatorLabel: 'contains', primary: true})
        schema.push({key: 'created', type: 'daterange', label: 'Created date', primary: true})
        schema.push({key: 'priority', type: 'select', label: 'Priority', primary: true,
          options: this.priorities.map(p => ({label: p.name, value: p.id}))})
        schema.push({key: 'status', type: 'select', label: 'Status', primary: true,
          options: this.statuses.map(s => ({label: s.name, value: s.id}))})
      }
      return schema
    },
    availableUsersForWatching() {
      const watcherIds = this.selectedTicketWatchers.map(w => w.user_id);
      return this.users.filter(user => !watcherIds.includes(user.id));
    },
    isBookingsProject() {
      return this.project.project_type === 'bookings'
    },
    ticketTableFields() {
      // Headers and cells never wrap. The ticket column is a fixed width and truncates long
      // titles with an ellipsis (full title on hover). The card scrolls horizontally.
      const col = (key, label, tdClass = 'text-nowrap') => ({ key, label, thClass: 'text-nowrap', tdClass })
      const fields = [
        { ...col('ticket', 'Ticket'), thStyle: { width: '420px', minWidth: '420px' } },
        col('ticket_type', 'Type'),
        col('assignee', 'Assignee'),
        col('reported_by', 'Reporter'),
        col('priority', 'Priority'),
        col('status', 'Status'),
        col('created', 'Created'),
        col('last_updated', 'Updated'),
        col('due_date', 'Due'),
        col('watchers', 'Watchers'),
      ]
      if (!this.isBookingsProject) {
        fields.push(col('actions', ''))
      }
      return fields
    },
  },
  data() {
    return {
      loading: false,
      edit_ticket_types: false,
      alertDialog: { show: false, title: '', message: '', variant: 'info', redirect: null },
      isDarkMode: true,
      date_format_cal: '',
      day_of_week: '',
      projectId: '',
      didInitialFetch: false,
      activeTabIndex: 0,
      companyId: localStorage.getItem('companyId'),
      companyGroupId: JSON.parse(localStorage.getItem('group')).id,
      date_format: JSON.parse(localStorage.getItem('group')).date_format,
      perPageOptions: [5, 10, 25, 50],
      filterValues: this.buildInitialFilterValues(),
      currentPage: 1,
      perPage: 10,
      totalRows: 10,
      project: {
        name: '',
        description: '',
        key: ''
      },
      tickets: [],
      users: [],
      selectedTicketWatchers: [],
      showAddWatchers: false,
      selectedWatchersToAdd: [],
      selectedTicketType: '',
      ticket_type_name: '',
      ticket_type_description: '',
      ticket_type_label_color: '#42b883',
      errs: {
        ticket_type: '',
        summary: '',
        ticket_type_name: '',
        time_estimate: ''
      },
      ticket_types: [],
      statuses: [],
      priorities: [
        {id: 1, name: "Highest", icon: 'fa fa-angle-double-up', color: '#ec250d'},
        {id: 2, name: "High", icon: 'fa fa-angle-up', color: '#ec250d'},
        {id: 3, name: "Medium", icon: 'tim-icons icon-simple-delete', color: '#ff8d72'},
        {id: 4, name: "Low", icon: 'fa fa-angle-down', color: '#1d8cf8'},
        {id: 5, name: "Lowest", icon: 'fa fa-angle-double-down', color: '#1d8cf8'},
      ],
      title: '',
      descriptionHtml: '',
      status: {id: 1},
      priority: {id: 3},
      time_estimate: '0m',
      ticket_type: {id: 1},
      assignee: {},
      new_ticket_due_date: null,
      modals: {
        new_ticket: false,
        configure_work_types: false
      },
      filters: {
        key: {
          value: '',
          actual: ''
        },
        summary: {
          value: '',
          actual: ''
        },
        ticket_type: {
          value: '',
          actual: ''
        },
        reported_by: {
          value: '',
          actual: ''
        },
        assigned_to: {
          value: '',
          actual: ''
        },
        priority: {
          value: '',
          actual: ''
        },
        status: {
          value: '',
          actual: ''
        },
        created:{
          value_from: '',
          actual_from: '',
          value_to: '',
          actual_to: '',
        },
        show_subtasks: {
          value: false,
          actual: false
        },
      }
    }
  },
  methods: {
    buildInitialFilterValues() {
      const q = this.$route.query || {}
      return {
        key: q.key || '',
        summary: q.summary || '',
        ticket_type: q.ticket_type ? Number(q.ticket_type) : '',
        status: q.status ? Number(q.status) : '',
        priority: q.priority ? Number(q.priority) : '',
        reported_by: q.reported_by || '',
        assigned_to: q.assigned_to || '',
        created: {mode: 'range', days: '', from: q.created_from || '', to: q.created_to || ''},
        show_subtasks: q.show_subtasks === '1' || q.show_subtasks === 'true',
      }
    },
    onFiltersChanged(values) {
      // Bridge the flat filter-bar values back into the existing filters object that getProject reads.
      const lookup = (arr, id) => arr.find(x => x.id === id) || ''
      this.filters.key.value = values.key || ''
      this.filters.summary.value = values.summary || ''
      this.filters.reported_by.value = values.reported_by || ''
      this.filters.assigned_to.value = values.assigned_to || ''
      this.filters.show_subtasks.value = !!values.show_subtasks
      this.filters.ticket_type.value = (values.ticket_type !== '' && values.ticket_type != null) ? lookup(this.ticket_types, values.ticket_type) : ''
      this.filters.status.value = (values.status !== '' && values.status != null) ? lookup(this.statuses, values.status) : ''
      this.filters.priority.value = (values.priority !== '' && values.priority != null) ? lookup(this.priorities, values.priority) : ''
      const c = values.created || {}
      let from = '', to = ''
      if (c.mode === 'days' && c.days) {
        const today = new Date()
        const dt = new Date()
        dt.setDate(dt.getDate() - Number(c.days))
        const fmt = d => d.getFullYear() + '-' + ('0' + (d.getMonth() + 1)).slice(-2) + '-' + ('0' + d.getDate()).slice(-2)
        from = fmt(dt)
        to = fmt(today)
      } else if (c.mode === 'range' && c.from && c.to) {
        from = c.from
        to = c.to
      }
      this.filters.created.value_from = from
      this.filters.created.value_to = to
      this.filterValues = {...this.filterValues, ...values, created: {...c}}
      if (!this.didInitialFetch) {
        // FilterBar (saved-filters-key) owns the first fetch and emits its initial @change on
        // mount. Preserve the URL page and honor URL-based filters (init=1) on this first call.
        this.didInitialFetch = true
        this.getProject(this.activeTabIndex, this.perPage, this.getOffset(), 1)
      } else {
        // Reset to page 1 BEFORE computing the offset so a stale page can't build an
        // out-of-range offset that 502s the backend.
        this.currentPage = 1
        this.getProject(this.activeTabIndex, this.perPage, this.getOffset(), 0)
      }
    },
    onPerPageChange(n) {
      this.perPage = n
      this.currentPage = 1
      this.getProject(this.activeTabIndex, this.perPage, this.getOffset(), 0)
    },
    escapeCSVValue(value) {
      if (typeof value === 'string') {
        value = value.replace(/"/g, '""')
        if (value.includes(',') || value.includes('\n')) {
          value = `"${value}"`
        }
      }
      return value
    },
    async downloadCSV() {
      let token = await this.getAuthToken()
      let config = { headers: { Authorization: token } }

      this.loading = true
      const csvData = await this.getAllTickets(config, 5000)
      this.loading = false

      let csv = 'Ticket key,Parent,Summary,Type,Reporter,Assignee,Created,Last updated,Due date,Priority,Status\n'
      csvData.forEach(row => {
        csv += [
          this.escapeCSVValue(row.ticket_key),
          this.escapeCSVValue(row.parent_ticket_key || ''),
          this.escapeCSVValue(row.title),
          this.escapeCSVValue(row.ticket_type_name || ''),
          this.escapeCSVValue(row.reporter_id ? row.reporter_name : ''),
          this.escapeCSVValue(row.assignee_id ? row.assignee_name : ''),
          this.escapeCSVValue(this.getLocalDateTime(row.time_created)),
          this.escapeCSVValue(row.last_updated ? this.getLocalDateTime(row.last_updated) : ''),
          this.escapeCSVValue(row.due_date ? this.getLocalDate(row.due_date) : ''),
          this.escapeCSVValue(this.getPriority(row.priority).name),
          this.escapeCSVValue(row.status_name || '')
        ].join(',')
        csv += '\n'
      })

      const anchor = document.createElement('a')
      anchor.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv)
      anchor.target = '_blank'
      anchor.download = 'project-tickets.csv'
      anchor.click()
    },
    async getAllTickets(config, count) {
      // Resolve the current filters the same way getProject does.
      let ticket_type = this.filters.ticket_type.value.id
      if (ticket_type === undefined || isNaN(ticket_type)) ticket_type = ''
      let status = this.filters.status.value.id
      if (status === undefined || isNaN(status)) status = ''
      let priority = this.filters.priority.value.id
      if (priority === undefined || isNaN(priority)) priority = ''

      let promises = []
      let pages = []
      let offset = 0
      while (offset <= this.totalRows) {
        let url = API_LOCATION + 'companies/' + this.companyId + '/groups/' + this.companyGroupId + '/projects/' + this.projectId +
          '?tab=' + this.activeTabIndex + '&count=' + count + '&offset=' + offset +
          '&summary=' + encodeURIComponent(this.filters.summary.value) +
          '&key=' + encodeURIComponent(this.filters.key.value) +
          '&reported_by=' + encodeURIComponent(this.filters.reported_by.value) +
          '&assigned_to=' + encodeURIComponent(this.filters.assigned_to.value) +
          '&ticket_type=' + encodeURIComponent(ticket_type) +
          '&status=' + encodeURIComponent(status) +
          '&priority=' + encodeURIComponent(priority) +
          '&created_to=' + encodeURIComponent(this.filters.created.value_to) +
          '&created_from=' + encodeURIComponent(this.filters.created.value_from) +
          '&include_subtasks=' + (this.filters.show_subtasks.value ? 1 : 0)

        promises.push(
          axios.get(url, config)
            .then(response => {
              pages.push(response.data.tickets)
            })
            .catch(err => {
              this.loading = false
              if (err.response && err.response.status == 401) {
                this.showAlert('Error', 'You do not have access to this feature. Reach out to your admin regarding your site permissions.', 'danger', '/')
              } else {
                this.showAlert('Error', 'Something went wrong', 'danger')
              }
            })
        )
        offset = offset + Number(count)
      }

      await Promise.all(promises)

      let arr = []
      pages.forEach(page => {
        arr = arr.concat(page)
      })
      return arr
    },
    hasPermissions(scope){
      let permissions = JSON.parse(localStorage.getItem('permissions'))
      const matchFound = permissions.some(element => element === scope);
      return matchFound
    },
    getInitials(firstName, lastName) {
      const first = firstName ? firstName.charAt(0).toUpperCase() : '';
      const last = lastName ? lastName.charAt(0).toUpperCase() : '';
      return first + last;
    },
    async loadTicketWatchers(ticket) {
      this.showAddWatchers = false;
      this.selectedWatchersToAdd = [];
      let token = await this.getAuthToken();
      let config = { headers: { Authorization: token } };
      let url = API_LOCATION + 'companies/' + this.companyId + '/groups/' + this.companyGroupId +
        '/projects/' + this.projectId + '/tickets/' + ticket.id + '/watch';

      axios.get(url, config)
        .then(response => {
          this.selectedTicketWatchers = response.data.watchers || [];
        })
        .catch(err => {
          this.loading = false;
          console.log(err);
          if(err.response?.status == 401){
            this.$router.push('/forbidden');
          } else {
            this.showAlert('Error', 'Failed to load watchers', 'danger');
          }
        });
    },
    async removeWatcher(watcher, ticket) {
      this.loading = true;
      let token = await this.getAuthToken();
      let config = { headers: { Authorization: token } };
      let url = API_LOCATION + 'companies/' + this.companyId + '/groups/' + this.companyGroupId +
        '/projects/' + this.projectId + '/tickets/' + ticket.id + '/watch/' + watcher.user_id;

      axios.delete(url, config)
        .then(response => {
          this.loading = false;
          this.showAlert('Success', `${watcher.first_name} ${watcher.last_name} is no longer watching this ticket`, 'success');
          this.loadTicketWatchers(ticket);
          this.getProject(this.activeTabIndex, this.perPage, this.getOffset());
        })
        .catch(err => {
          this.loading = false;
          console.log(err);
          if(err.response?.status == 401){
            this.$router.push('/forbidden');
          } else if(err.response?.status == 500){
            this.showAlert('Error', err.response.data, 'danger')
          }
        });
    },
    async addWatchersInline(ticket) {
      if (this.selectedWatchersToAdd.length === 0) {
        this.showAlert('Error', 'Please select at least one user to add as a watcher', 'danger');
        return;
      }

      this.loading = true;
      let token = await this.getAuthToken();
      let config = { headers: { Authorization: token } };
      let url = API_LOCATION + 'companies/' + this.companyId + '/groups/' + this.companyGroupId +
        '/projects/' + this.projectId + '/tickets/' + ticket.id + '/watch';

      let data = {
        user_ids: this.selectedWatchersToAdd
      };

      axios.post(url, data, config)
        .then(response => {
          this.loading = false;
          this.selectedWatchersToAdd = [];
          this.showAddWatchers = false;
          this.showAlert('Success', 'Watchers have been added', 'success');
          this.loadTicketWatchers(ticket);
          this.getProject(this.activeTabIndex, this.perPage, this.getOffset());
        })
        .catch(err => {
          this.loading = false;
          console.log(err);
          if(err.response?.status == 401){
            this.$router.push('/forbidden');
          } else if(err.response?.status == 500){
            this.showAlert('Error', err.response.data, 'danger')
          } else {
            this.showAlert('Error', 'Failed to add watchers', 'danger');
          }
        });
    },
    addNewTicketType(){
      this.edit_ticket_types = true
      this.ticket_type_name = ''
      this.ticket_type_description = ''
      this.selectedTicketType = ''
      this.ticket_type_label_color =  '#42b883'
    },
    getPriority(priority){
      let p = _.find(this.priorities, {id: priority})
      return p || {id: null, name: '-', icon: '', color: 'transparent'}
    },
    configureWorkTypes(){
      this.modals.configure_work_types = true
    },
    createNewTicket(){
      this.isDarkMode = !document.body.classList.contains('white-content')
      this.modals.new_ticket = true
      this.status = {id: 1}
      this.priority = {id: 3}
      this.descriptionHtml = ''
      this.title = ''
      if (this.$refs.descriptionEditor) this.$refs.descriptionEditor.clear()
      this.time_estimate = '0m'
      this.new_ticket_due_date = null
      this.ticket_type = this.ticket_types.filter(t => t.archived === 0)[0]
    },
    closeTicketDrawer(){
      this.modals.new_ticket = false
      this.clearTicket()
    },
    getEditorContent() {
      return this.$refs.descriptionEditor ? this.$refs.descriptionEditor.getContent() : '';
    },
    clearTicket(){
      this.status = {id: 1}
      this.priority = {id: 3}
      this.descriptionHtml = ''
      this.title = ''
      this.new_ticket_due_date = null
      this.ticket_type = this.ticket_types.filter(t => t.archived === 0)[0]
      if (this.$refs.descriptionEditor) this.$refs.descriptionEditor.clear()
    },
    switchTab(index, count, offset, init){
      this.activeTabIndex = index
      this.currentPage = 1
      this.getProject(this.activeTabIndex, count, offset, init)
    },
    getLocalDateTime(d){
      if (!d) return ''
      const date = formatGroupDate(d, { dayOfWeek: false })
      const time = formatGroupTime(d)
      if (!date && !time) return ''
      return `${date}, ${time}`
    },
    getLocalDate(d){
      if (!d) return '-'
      return formatDate(d.substring(0, 10), this.date_format)
    },
    isOverdue(ticket){
      if (!ticket.due_date) return false
      const dueStr = ticket.due_date.substring(0, 10)
      const today = new Date().toISOString().substring(0, 10)
      const doneStatus = this.statuses.find(s => s.lifecycle_status === 'done')
      if (doneStatus && ticket.status === doneStatus.id) return false
      return dueStr < today
    },
    getAuthToken() {
      return new Promise ( function(resolve) {
        Auth.currentSession()
          .then(data =>{
            resolve(data.idToken.jwtToken)
          })
          .catch(err => console.log(err))
      })
    },
    async createTicketType(){
      if(this.ticket_type_name === ''){
        this.errs.ticket_type_name = 'Ticket type name cannot be empty'
          return
      } else {
        this.errs.ticket_type_name = ''
      }

      this.loading = true
      let token = await this.getAuthToken()
      let config = { headers: { Authorization: token } }
      let url = API_LOCATION + 'companies/' + this.companyId + '/groups/' + this.companyGroupId + '/projects/' + this.projectId + '/ticket-types'

      let data = {
        name: this.ticket_type_name,
        description: this.ticket_type_description,
        label_color: this.ticket_type_label_color
      }

      axios.post(url, data, config)
        .then(response => {
          this.loading = false
          this.edit_ticket_types = false
          this.ticket_types = response.data.ticket_types
        })
        .catch(err => {
          this.loading = false
          if(err.response && err.response.status == 401){
            this.$router.push('/forbidden')
          } else {
            this.showAlert('Error', 'Something went wrong', 'danger')
          }
        })
    },
    editTicketType(item){
      this.selectedTicketType = {...item}
      this.edit_ticket_types = true
      this.ticket_type_description = this.selectedTicketType.description
      this.ticket_type_label_color = this.selectedTicketType.label_color
      this.ticket_type_name = this.selectedTicketType.name
    },
    async updateTicketType(){
      if(this.ticket_type_name === ''){
        this.errs.ticket_type_name = 'Ticket type name cannot be empty'
        return
      } else {
        this.errs.ticket_type_name = ''
      }

      this.loading = true
      let token = await this.getAuthToken()
      let config = { headers: { Authorization: token } }
      let url = API_LOCATION + 'companies/' + this.companyId + '/groups/' + this.companyGroupId + '/projects/'
        + this.projectId + '/ticket-types/' + this.selectedTicketType.id

      let data = {
        name: this.ticket_type_name,
        description: this.ticket_type_description,
        label_color: this.ticket_type_label_color
      }

      axios.put(url, data, config)
        .then(response => {
          this.loading = false
          this.edit_ticket_types = false
          this.ticket_types = response.data.ticket_types
        })
        .catch(err => {
          this.loading = false
          if(err.response && err.response.status == 401){
            this.$router.push('/forbidden')
          } else {
            this.showAlert('Error', 'Something went wrong', 'danger')
          }
        })
    },
    async archiveTicketType(item){
      this.loading = true
      let token = await this.getAuthToken()
      let config = { headers: { Authorization: token } }
      let url = API_LOCATION + 'companies/' + this.companyId + '/groups/'
        + this.companyGroupId + '/projects/' + this.projectId + '/ticket-types/' + item.id

      axios.delete(url, config)
        .then(response => {
          this.loading = false
          this.ticket_types = response.data.ticket_types
        })
        .catch(err => {
          this.loading = false
          if(err.response && err.response.status === 401){
            this.$router.push('/forbidden')
          } else if(err.response && err.response.status === 500){
            this.showAlert('Error', err.response.data, 'danger')
          } else {
            this.showAlert('Error', 'Something went wrong', 'danger')
          }
        })
    },
    validateTicket(){
      let errors = 0
      if(this.title === ''){
        this.errs.summary = 'Ticket summary cannot be empty'
        errors++
      } else {
        this.errs.summary = ''
      }

      if(!this.isValidTimeString(this.time_estimate)){
        this.errs.time_estimate = 'Invalid time format'
        errors++
      } else {
        this.errs.time_estimate = ''
      }

      return !(errors > 0)
    },
    isValidTimeString(timeString) {
      if(timeString === ''){
        this.time_estimate = null
      }
      const validOrder = ['w', 'd', 'h', 'm'];
      const regex = /(\d+)\s*(w|d|h|m)/g;

      let seenUnits = new Set();
      let lastIndex = -1;
      let match;
      let inputTrimmed = timeString.trim();

      // Reconstruct a valid string as we parse to compare with the original (normalized)
      let reconstructed = '';

      while ((match = regex.exec(inputTrimmed)) !== null) {
        const [full, number, unit] = match;

        if (seenUnits.has(unit)) return false; // Duplicate unit
        seenUnits.add(unit);

        const indexInOrder = validOrder.indexOf(unit);
        if (indexInOrder <= lastIndex) return false; // Units out of order
        lastIndex = indexInOrder;

        reconstructed += (reconstructed ? ' ' : '') + `${number}${unit}`;
      }

      // Ensure all characters were parsed and structure is clean
      return reconstructed === inputTrimmed;
    },
    parseTimeStringToMinutes(timeString) {
      const regex = /(\d+)\s*(w|d|h|m)/g;
      let totalMinutes = 0;
      let match;

      const multipliers = {
        w: 7 * 24 * 60,
        d: 24 * 60,
        h: 60,
        m: 1
      };

      while ((match = regex.exec(timeString)) !== null) {
        const value = parseInt(match[1], 10);
        const unit = match[2];
        totalMinutes += value * (multipliers[unit] || 0);
      }

      return totalMinutes;
    },
    async createTicket(){
      if(!this.validateTicket()){
        return
      }

      this.loading = true
      let token = await this.getAuthToken()
      let config = { headers: { Authorization: token } }
      let url = API_LOCATION + 'companies/' + this.companyId + '/groups/' + this.companyGroupId + '/projects/' + this.projectId + '/tickets'

      let data = {
        title: this.title,
        description: this.getEditorContent(),
        assignee: this.assignee.id,
        status: this.status.id,
        priority: this.priority.id,
        ticket_type: this.ticket_type.id,
        time_estimate_minutes: this.parseTimeStringToMinutes(this.time_estimate),
        due_date: this.new_ticket_due_date
      }

      axios.post(url, data, config)
        .then(response => {
          this.loading = false
          this.modals.new_ticket = false
          this.getProject(this.activeTabIndex, this.perPage, this.getOffset(), 0)
          this.clearTicket()
        })
        .catch(err => {
          this.loading = false
          if(err.response && err.response.status == 401){
            this.$router.push('/forbidden')
          } else {
            this.showAlert('Error', 'Something went wrong', 'danger')
          }
        })
    },
    async archiveTicket(item){
      this.loading = true
      let token = await this.getAuthToken()
      let config = { headers: { Authorization: token } }
      let url = API_LOCATION + 'companies/' + this.companyId + '/groups/' + this.companyGroupId +
        '/projects/' + this.projectId + '/tickets/' + item.id

      axios.delete(url, config)
        .then(response => {
          this.loading = false
          this.showAlert('Success', 'Ticket has been archived', 'success')
          this.getProject(this.activeTabIndex, this.perPage, this.getOffset(), 0)
        })
        .catch(err => {
          this.loading = false
          if(err.response && err.response.status == 401){
            this.$router.push('/forbidden')
          } else {
            this.showAlert('Error', 'Something went wrong', 'danger')
          }
        })
    },
    async modifyTicket(item, event){
      event.preventDefault()
      this.loading = true
      let token = await this.getAuthToken()
      let config = { headers: { Authorization: token } }
      let url = API_LOCATION + 'companies/' + this.companyId + '/groups/' + this.companyGroupId +
        '/projects/' + this.projectId + '/tickets/' + item.id + '/watch'

      axios.post(url, {}, config)
        .then(response => {
          this.loading = false
          const result = response.data
          this.showAlert('Success', result.message, 'success')
          this.getProject(this.activeTabIndex, this.perPage, this.getOffset())
          // Reload watchers for the current ticket to update the popover
          this.loadTicketWatchers(item)
        })
        .catch(err => {
          this.loading = false
          console.log(err)
          if(err.response?.status == 401){
            this.$router.push('/forbidden')
          } else if(err.response?.status == 500){
            this.showAlert('Error', err.response.data, 'danger')
          } else {
            this.showAlert('Error', 'Failed to toggle watch status', 'danger')
          }
        })
    },
    showAlert(title, message, variant, redirect = null) {
      this.alertDialog = { show: true, title, message, variant, redirect }
    },
    closeAlert() {
      const redirect = this.alertDialog.redirect
      this.alertDialog = { show: false, title: '', message: '', variant: 'info', redirect: null }
      if (redirect) this.$router.push(redirect)
    },
    async getProject(tab, count, offset, init, source){
      if(source === 'filter'){
        this.currentPage = 1
      }
      this.filters.key.actual = this.filters.key.value
      this.filters.summary.actual = this.filters.summary.value
      this.filters.reported_by.actual = this.filters.reported_by.value
      this.filters.assigned_to.actual = this.filters.assigned_to.value
      this.filters.show_subtasks.actual = this.filters.show_subtasks.value
      this.filters.created.actual_to = this.filters.created.value_to
      this.filters.created.actual_from = this.filters.created.value_from
      this.filters.ticket_type.actual = this.filters.ticket_type.value.name
      let ticket_type = this.filters.ticket_type.value.id
      if(init && this.$route.query.ticket_type !== ''){
        ticket_type = Number(this.$route.query.ticket_type)
      }
      if(ticket_type === undefined || isNaN(ticket_type)){
        ticket_type = ''
      }

      this.filters.status.actual = this.filters.status.value.name
      let status = this.filters.status.value.id
      if(init && this.$route.query.status !== ''){
        status = Number(this.$route.query.status)
      }
      if(status === undefined || isNaN(status)){
        status = ''
      }

      this.filters.priority.actual = this.filters.priority.value.name
      let priority = this.filters.priority.value.id
      if(init && this.$route.query.priority !== ''){
        priority = Number(this.$route.query.priority)
      }
      if(priority === undefined || isNaN(priority)){
        priority = ''
      }

      this.loading = true
      let token = await this.getAuthToken()
      let config = { headers: { Authorization: token } }
      let url = API_LOCATION + 'companies/' + this.companyId + '/groups/' + this.companyGroupId + '/projects/' + this.projectId +
        '?tab=' + tab + '&count=' + count + '&offset=' + offset +
        "&summary=" + encodeURIComponent(this.filters.summary.actual)
        +"&key=" + encodeURIComponent(this.filters.key.actual)
        +"&reported_by=" + encodeURIComponent(this.filters.reported_by.actual)
        +"&assigned_to=" + encodeURIComponent(this.filters.assigned_to.actual)
        +"&ticket_type=" + encodeURIComponent(ticket_type)
        +"&status=" + encodeURIComponent(status)
        +"&priority=" + encodeURIComponent(priority)
        +"&created_to=" + encodeURIComponent(this.filters.created.actual_to)
        +"&created_from=" + encodeURIComponent(this.filters.created.actual_from)
        +"&include_subtasks=" + (this.filters.show_subtasks.actual ? 1 : 0)

      axios.get(url, config)
        .then(response => {
          this.loading = false
          this.project = response.data.project
          this.ticket_types = response.data.ticket_types
          this.statuses = response.data.statuses
          this.users = response.data.users
          this.tickets = response.data.tickets
          this.totalRows = response.data.total_rows

          // If current page is beyond available data, reset to page 1
          if (this.totalRows > 0 && offset >= this.totalRows) {
            this.currentPage = 1
            this.getProject(tab, count, this.getOffset(), 0)
            return
          }

          if(init && this.$route.query.status) {
            this.$nextTick(() => {
              let statusObj = _.find(this.statuses, {id: status})
              this.filters.status.value = statusObj
              this.filters.status.actual = statusObj ? statusObj.name : ''
            });
          }
          if(init && this.$route.query.ticket_type) {
            this.$nextTick(() => {
              let typeObj = _.find(this.ticket_types, {id: ticket_type})
              this.filters.ticket_type.value = typeObj
              this.filters.ticket_type.actual = typeObj ? typeObj.name : ''
            });
          }
          if(init && this.$route.query.priority) {
            this.$nextTick(() => {
              let priorityObj = _.find(this.priorities, {id: priority})
              this.filters.priority.value = priorityObj
              this.filters.priority.actual = priorityObj ? priorityObj.name : ''
            });
          }

          this.addQueryParameters()
        })
        .catch(err => {
          this.loading = false
          if(err.response && err.response.status == 401){
            this.$router.push('/projects?count=10&page=1')
          } else {
            this.showAlert('Error', 'Something went wrong', 'danger')
          }
        })
    },
    addQueryParameters() {
      this.$nextTick(() => {
        this.currentPage = this.$route.query.page
      });

      const newQuery = {
        ...this.$route.query,
        tab: this.activeTabIndex,
        count: this.perPage,
        page: this.currentPage,
        key: this.filters.key.value,
        summary: this.filters.summary.value,
        reported_by: this.filters.reported_by.value,
        assigned_to: this.filters.assigned_to.value,
        ticket_type: this.filters.ticket_type.value.hasOwnProperty('id') ? this.filters.ticket_type.value.id : this.filters.ticket_type.value,
        status: this.filters.status.value.hasOwnProperty('id') ? this.filters.status.value.id : this.filters.status.value,
        priority: this.filters.priority.value.hasOwnProperty('id') ? this.filters.priority.value.id : this.filters.priority.value,
        created_to: this.filters.created.value_to,
        created_from: this.filters.created.value_from
      }
      this.$router.replace({query: newQuery}).catch(err => {
        if (err.name !== 'NavigationDuplicated') {
          throw err;
        }
      });
    },
    getOffset(){
      const offset = Number((this.currentPage * this.perPage) - this.perPage)
      // Clamp to >= 0. A stale/NaN/0 page can build a negative/out-of-range offset that 502s the backend.
      return (isNaN(offset) || offset < 0) ? 0 : offset
    },
  },
  watch: {
    'modals.new_ticket'(open) {
      if (open) {
        document.addEventListener('keydown', this._onTicketDrawerEsc)
      } else {
        document.removeEventListener('keydown', this._onTicketDrawerEsc)
      }
    },
  },
  created() {
    this._onTicketDrawerEsc = (e) => {
      if (e.key === 'Escape' && this.modals.new_ticket) {
        this.closeTicketDrawer()
      }
    }
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this._onTicketDrawerEsc)
  },
  mounted() {
    this.projectId = this.$route.params.id
    this.date_format_cal = this.date_format
    this.date_format_cal =  this.date_format_cal.replaceAll('Y', 'y')
    this.date_format_cal = this.date_format_cal.replaceAll('D', 'd')
    if(JSON.parse(localStorage.getItem('group')).show_day_of_week){
      this.day_of_week = 'ddd, '
    }
    this.filters = {
      key: {
        value: this.$route.query.key ? this.$route.query.key : '',
        actual: this.$route.query.key ? this.$route.query.key : ''
      },
      summary: {
        value: this.$route.query.summary ? this.$route.query.summary : '',
        actual: this.$route.query.summary ? this.$route.query.summary : ''
      },
      ticket_type: {
        value: this.$route.query.ticket_type ? this.$route.query.ticket_type : '',
        actual: this.$route.query.ticket_type ? this.$route.query.ticket_type : ''
      },
      status: {
        value: this.$route.query.status ? this.$route.query.status : '',
        actual: this.$route.query.status ? this.$route.query.status : ''
      },
      reported_by: {
        value: this.$route.query.reported_by ? this.$route.query.reported_by : '',
        actual: this.$route.query.reported_by ? this.$route.query.reported_by : ''
      },
      assigned_to: {
        value: this.$route.query.reported_by ? this.$route.query.reported_by : '',
        actual: this.$route.query.reported_by ? this.$route.query.reported_by : ''
      },
      priority: {
        value: this.$route.query.priority ? this.$route.query.priority : '',
        actual: this.$route.query.priority ? this.$route.query.priority : ''
      },
      created: {
        value_to: this.$route.query.created_to ? this.$route.query.created_to : '',
        actual_to: this.$route.query.created_to ? this.$route.query.created_to : '',
        value_from: this.$route.query.created_from ? this.$route.query.created_from : '',
        actual_from: this.$route.query.created_from ? this.$route.query.created_from : ''
      },
      show_subtasks: {
        value: this.$route.query.show_subtasks === '1' || this.$route.query.show_subtasks === 'true',
        actual: false
      },
    }

    this.perPage = this.$route.query.count ? Number(this.$route.query.count) : 10
    this.currentPage = this.$route.query.page ? Number(this.$route.query.page) : 1
    let tab = this.$route.query.hasOwnProperty('tab') ? this.$route.query.tab : 0
    // Set the active tab WITHOUT fetching. FilterBar (saved-filters-key) drives the initial
    // fetch via its initial @change → onFiltersChanged; fetching here would double-fetch.
    this.activeTabIndex = Number(tab)
  }
}
</script>

<style scoped>
/* Watcher Popover Styles */
::v-deep(.watcher-popover) {
  background-color: #ffffff !important;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15), 0 4px 8px rgba(0, 0, 0, 0.1) !important;
}

::v-deep(.watcher-popover .popper__arrow::after) {
  border-bottom-color: #ffffff !important;
}

.watcher-popover-content {
  color: #344675;
}

.watcher-label {
  font-weight: bold;
  font-size: 10px;
  color: #525f7f !important;
  letter-spacing: 0.5px;
}

.watcher-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  padding: 8px;
  background-color: #f6f9fc;
  border-radius: 4px;
  border: 1px solid #e9ecef;
}

.watcher-name {
  font-size: 14px;
  color: #344675 !important;
  font-weight: 500;
}

.no-watchers-message {
  text-align: center;
  padding: 16px 12px;
  background-color: #f6f9fc;
  border-radius: 4px;
  border: 1px solid #e9ecef;
}

.no-watchers-message p {
  color: #8898aa !important;
  font-size: 13px;
}
</style>
